import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { postToken } from '../api/auth';
import { AUTH_EXPIRED_EVENT } from '../api/client';
import type { JwtPayload, LicenseType, Scope, TokenRequest } from '../api/types';
import { useToastStore } from '../stores/toastStore';
import { decodeJwtPayload } from './jwt';
import { TokenStore } from './TokenStore';

/** Time before token expiry at which we warn the user. */
const EXPIRY_WARNING_MS = 5 * 60 * 1000;

export interface AuthenticatedSession {
  tenantId: string;
  userId: string;
  licenseType: LicenseType;
  scopes: string[];
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  session: AuthenticatedSession | null;
  login: (credentials: TokenRequest) => Promise<void>;
  logout: () => void;
  hasScope: (scope: Scope) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function sessionFromPayload(payload: JwtPayload): AuthenticatedSession {
  return {
    tenantId: payload.tenant_id,
    userId: payload.user_id,
    licenseType: payload.license_type,
    scopes: payload.scopes,
  };
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [session, setSession] = useState<AuthenticatedSession | null>(null);
  const warnTimerRef = useRef<number | null>(null);
  const pushToast = useToastStore((state) => state.push);

  const clearWarnTimer = useCallback(() => {
    if (warnTimerRef.current !== null) {
      window.clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
  }, []);

  const scheduleExpiryWarning = useCallback(
    (expiresAtMs: number) => {
      clearWarnTimer();
      const warnAt = expiresAtMs - EXPIRY_WARNING_MS - Date.now();
      if (warnAt <= 0) {
        return;
      }
      warnTimerRef.current = window.setTimeout(() => {
        pushToast({
          tone: 'warning',
          title: 'Tu sesión expira pronto',
          description: 'Por seguridad tendrás que volver a iniciar sesión en breve.',
          durationMs: 10_000,
        });
      }, warnAt);
    },
    [clearWarnTimer, pushToast],
  );

  const applyToken = useCallback(
    (accessToken: string, expiresInSeconds: number): boolean => {
      const payload = decodeJwtPayload(accessToken);
      if (payload === null) {
        return false;
      }
      const expiresAtMs = payload.exp * 1000;
      TokenStore.set({ accessToken, expiresAtMs });
      setSession(sessionFromPayload(payload));
      scheduleExpiryWarning(expiresAtMs);
      // We trust the JWT `exp` over the `expiresIn` hint, but log a sanity check.
      const drift = Math.abs(expiresAtMs - Date.now() - expiresInSeconds * 1000);
      if (drift > 60_000) {
        // eslint-disable-next-line no-console
        console.warn('JWT exp claim differs from expiresIn by >60s; using exp.');
      }
      return true;
    },
    [scheduleExpiryWarning],
  );

  const logout = useCallback(() => {
    clearWarnTimer();
    TokenStore.clear();
    setSession(null);
  }, [clearWarnTimer]);

  const login = useCallback(
    async (credentials: TokenRequest) => {
      const response = await postToken(credentials);
      const ok = applyToken(response.accessToken, response.expiresIn);
      if (!ok) {
        TokenStore.clear();
        throw new Error('El token recibido es inválido.');
      }
    },
    [applyToken],
  );

  // Listen for forced logouts dispatched by the API client on 401 responses.
  useEffect(() => {
    const handler = (): void => {
      if (session !== null) {
        pushToast({
          tone: 'warning',
          title: 'Sesión finalizada',
          description: 'Vuelve a iniciar sesión para continuar.',
        });
      }
      logout();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
    };
  }, [logout, pushToast, session]);

  // Re-hydrate from TokenStore on mount (e.g. soft remount during HMR).
  useEffect(() => {
    const stored = TokenStore.get();
    if (stored === null || stored.expiresAtMs <= Date.now()) {
      return;
    }
    const payload = decodeJwtPayload(stored.accessToken);
    if (payload === null) {
      TokenStore.clear();
      return;
    }
    setSession(sessionFromPayload(payload));
    scheduleExpiryWarning(stored.expiresAtMs);
  }, [scheduleExpiryWarning]);

  useEffect(() => clearWarnTimer, [clearWarnTimer]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: session !== null,
      session,
      login,
      logout,
      hasScope: (scope) => session !== null && session.scopes.includes(scope),
    }),
    [login, logout, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
